import React, { useState, useEffect, useCallback } from 'react';
import { Crown, Calendar, CreditCard, Check, X, AlertCircle, FileSignature, DollarSign } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSettings = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [commissionStats, setCommissionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSubscription = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
      
      // If on contract tier, fetch commission stats
      if (response.data?.plan_id === 'contract') {
        try {
          const commissionResponse = await axios.get(`${API_URL}/api/subscriptions/my-commission-stats`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCommissionStats(commissionResponse.data);
        } catch (err) {
          console.error('Error fetching commission stats:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load subscription details',
      });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const handleJoinContract = async () => {
    if (!window.confirm('Join The Contract tier? You will have access to all Premium features, but 20% of your sales will automatically go to the admin.')) {
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/subscribe/contract`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Welcome to The Contract!',
        description: 'You now have access to all Premium features. 20% of your sales will go to the admin.',
      });

      fetchSubscription();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to join The Contract tier',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Subscription Cancelled',
        description: 'Your subscription will remain active until the end of the billing period.',
      });

      fetchSubscription();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to cancel subscription',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Subscription Reactivated',
        description: 'Your subscription has been reactivated successfully.',
      });

      fetchSubscription();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reactivate subscription',
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-400">
        Loading subscription details...
      </div>
    );
  }

  const isFree = subscription?.plan_id === 'free';
  const isStandard = subscription?.plan_id === 'standard';
  const isContract = subscription?.plan_id === 'contract';
  const isCancelling = subscription?.cancel_at_period_end;
  const plan = subscription?.plan;

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-2xl font-bold text-white">Current Plan</h3>
                {isStandard && (
                  <Badge className="bg-gradient-to-r from-yellow-400 to-purple-500 text-black">
                    <Crown className="w-3 h-3 mr-1" />
                    Standard
                  </Badge>
                )}
                {isContract && (
                  <Badge className="bg-gradient-to-r from-emerald-400 to-cyan-500 text-black">
                    <FileSignature className="w-3 h-3 mr-1" />
                    The Contract
                  </Badge>
                )}
                {isFree && (
                  <Badge className="bg-gray-700 text-white">
                    Free
                  </Badge>
                )}
              </div>
              <p className="text-gray-400">
                {plan?.name} Plan - {plan?.price === 0 ? 'Free' : `$${plan?.price || 0}/${plan?.interval || 'month'}`}
              </p>
              {isContract && (
                <p className="text-emerald-400 text-sm mt-1">
                  Admin receives 20% of your sales automatically
                </p>
              )}
            </div>
            {isCancelling && (
              <Badge variant="destructive" className="bg-red-500/20 text-red-400 border-red-500">
                <AlertCircle className="w-3 h-3 mr-1" />
                Cancelling
              </Badge>
            )}
          </div>

          {/* Billing Info */}
          {isStandard && subscription.current_period_end && (
            <div className="flex items-center gap-2 text-gray-400 mb-4">
              <Calendar className="w-4 h-4" />
              <span>
                {isCancelling ? 'Access until' : 'Renews on'}{' '}
                {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            </div>
          )}

          {/* Features */}
          <div className="space-y-2 mb-6">
            <h4 className="font-semibold text-white mb-3">What&apos;s included:</h4>
            {plan?.features?.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isContract ? 'text-emerald-400' : isStandard ? 'text-yellow-400' : 'text-green-500'}`} />
                <span className="text-gray-300">{feature}</span>
              </div>
            ))}
            {isFree && (
              <div className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 line-through">Upload content</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            {isFree && (
              <>
                <Button
                  onClick={handleUpgrade}
                  className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Standard
                </Button>
                <Button
                  onClick={handleJoinContract}
                  disabled={actionLoading}
                  className="bg-gradient-to-r from-emerald-400 to-cyan-500 hover:from-emerald-500 hover:to-cyan-600 text-black font-bold"
                >
                  <FileSignature className="w-4 h-4 mr-2" />
                  Join The Contract
                </Button>
              </>
            )}
            {isStandard && !isCancelling && (
              <Button
                onClick={handleCancel}
                disabled={actionLoading}
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-500/10"
              >
                Cancel Subscription
              </Button>
            )}
            {isStandard && isCancelling && (
              <Button
                onClick={handleReactivate}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Reactivate Subscription
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Commission Stats for Contract Tier */}
      {isContract && commissionStats && (
        <Card className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-emerald-500/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold text-white">Commission Details</h3>
            </div>
            
            <p className="text-gray-300 mb-4">{commissionStats.message}</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-gray-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Total Sales</p>
                <p className="text-2xl font-bold text-white">${commissionStats.stats?.total_sales?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Commission Paid</p>
                <p className="text-2xl font-bold text-red-400">${commissionStats.stats?.total_commission_paid?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Net Earnings</p>
                <p className="text-2xl font-bold text-emerald-400">${commissionStats.stats?.net_earnings?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Transactions</p>
                <p className="text-2xl font-bold text-white">{commissionStats.stats?.transaction_count || 0}</p>
              </div>
            </div>

            {commissionStats.recent_transactions?.length > 0 && (
              <div>
                <h4 className="font-semibold text-white mb-3">Recent Transactions</h4>
                <div className="space-y-2">
                  {commissionStats.recent_transactions.slice(0, 5).map((tx) => (
                    <div key={tx.id || tx.created_at} className="flex justify-between items-center py-2 border-b border-gray-700">
                      <div>
                        <p className="text-white">{tx.transaction_type}</p>
                        <p className="text-sm text-gray-400">{new Date(tx.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white">${tx.sale_amount?.toFixed(2)}</p>
                        <p className="text-sm text-red-400">-${tx.commission_amount?.toFixed(2)} commission</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upgrade Prompt for Free Users */}
      {isFree && (
        <>
          <Card className="bg-gradient-to-r from-yellow-500/10 to-purple-500/10 border-yellow-500/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                  <Crown className="w-6 h-6 text-black" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-white mb-2">
                    Unlock Content Creation
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Upgrade to Own Ya Royalties for just $30/year (that's $2.50/month) and start uploading your content to reach thousands of viewers.
                  </p>
                  <Button
                    onClick={handleUpgrade}
                    className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
                  >
                    View Plans
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* The Contract Tier Promotion */}
          <Card className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-emerald-500/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <FileSignature className="w-6 h-6 text-black" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-white mb-2">
                    Join The Contract
                  </h4>
                  <p className="text-gray-300 mb-2">
                    <span className="text-emerald-400 font-bold">Free forever.</span> Get all Premium features at no cost.
                  </p>
                  <p className="text-gray-400 text-sm mb-4">
                    Admin receives 20% of your sales automatically. Perfect for new creators who want full access without upfront costs.
                  </p>
                  <Button
                    onClick={handleJoinContract}
                    disabled={actionLoading}
                    className="bg-gradient-to-r from-emerald-400 to-cyan-500 hover:from-emerald-500 hover:to-cyan-600 text-black font-bold"
                  >
                    {actionLoading ? 'Processing...' : 'Join The Contract'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Payment Method (Placeholder) */}
      {isStandard && (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-5 h-5 text-gray-400" />
              <h3 className="text-lg font-semibold text-white">Payment Method</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Demo Mode: In production, Stripe payment method will be displayed here.
            </p>
            <Button
              variant="outline"
              className="border-gray-600 hover:bg-gray-700 text-white"
              disabled
            >
              Update Payment Method
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Billing History (Placeholder) */}
      {isStandard && (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Billing History</h3>
            <p className="text-gray-400 text-sm mb-4">
              Demo Mode: In production, your billing history will appear here.
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-3 border-b border-gray-700">
                <div>
                  <p className="text-white font-medium">Standard Plan</p>
                  <p className="text-sm text-gray-400">
                    {new Date(subscription.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="text-white font-semibold">$50.00</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View All Plans Section */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Available Plans</h3>
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="border-gray-600 hover:bg-gray-700 text-white"
            >
              View Full Details
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Basic Plan - FREE (View Only) */}
            <div className={`p-4 rounded-lg border-2 ${subscription?.plan_id === 'basic' || subscription?.plan_id === 'free' ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 bg-gray-800/50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                  <span className="text-white text-sm">⚡</span>
                </div>
                <h4 className="font-bold text-white">Basic</h4>
                <Badge className="bg-blue-500 text-white text-xs">FREE</Badge>
              </div>
              <p className="text-2xl font-bold text-blue-400 mb-2">FREE</p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• View all content</li>
                <li>• Follow creators</li>
                <li className="text-yellow-400">• Cannot upload content</li>
              </ul>
            </div>

            {/* On Ya Royalties */}
            <div className={`p-4 rounded-lg border-2 ${subscription?.plan_id === 'on_ya_royalties' || subscription?.plan_id === 'standard' ? 'border-yellow-500 bg-yellow-500/10' : 'border-gray-700 bg-gray-800/50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center">
                  <Crown className="w-4 h-4 text-black" />
                </div>
                <h4 className="font-bold text-white">On Ya Royalties</h4>
                <Badge className="bg-yellow-500 text-black text-xs">POPULAR</Badge>
              </div>
              <p className="text-2xl font-bold text-yellow-400 mb-2">$50<span className="text-sm text-gray-400">/mo</span></p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• Upload unlimited content</li>
                <li>• Creator dashboard</li>
                <li className="text-green-400">• No commission on sales</li>
              </ul>
            </div>

            {/* The Contract */}
            <div className={`p-4 rounded-lg border-2 ${subscription?.plan_id === 'contract' ? 'border-emerald-500 bg-emerald-500/10' : 'border-gray-700 bg-gray-800/50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-500 flex items-center justify-center">
                  <FileSignature className="w-4 h-4 text-black" />
                </div>
                <h4 className="font-bold text-white">The Contract</h4>
                <Badge className="bg-emerald-500 text-black text-xs">FREE</Badge>
              </div>
              <p className="text-2xl font-bold text-emerald-400 mb-2">FREE</p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• All premium features</li>
                <li>• Upload unlimited</li>
                <li className="text-yellow-400">• 20% commission to admin</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSettings;